import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BarChart3, TrendingUp, Users, Package, Eye, MousePointer, Star } from 'lucide-react';
import axios from 'axios';

const AdminAnalytics = () => {
  const [analytics, setAnalytics] = useState({
    general_stats: {
      total_approved_products: 0,
      total_whatsapp_clicks: 0,
      avg_clicks_per_product: 0
    },
    top_products: [],
    producer_stats: [],
    category_stats: []
  });
  const [loading, setLoading] = useState(true);
  const { API } = useContext(AuthContext);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/analytics`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('pt-BR').format(num);
  };

  const getClickPercentage = (clicks, maxClicks) => {
    if (maxClicks === 0) return 0;
    return Math.round((clicks / maxClicks) * 100);
  };

  if (loading) {
    return (
      <Card className="card-feira">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            Carregando Analytics...
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const maxProductClicks = analytics.top_products.length > 0 
    ? Math.max(...analytics.top_products.map(p => p.whatsapp_clicks || 0))
    : 0;

  const maxProducerClicks = analytics.producer_stats.length > 0
    ? Math.max(...analytics.producer_stats.map(p => p.total_clicks || 0))
    : 0;

  return (
    <div className="space-y-6">
      {/* Estatísticas Gerais */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="card-feira">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total de Produtos</p>
                <p className="text-3xl font-bold text-green-600">
                  {formatNumber(analytics.general_stats.total_approved_products)}
                </p>
              </div>
              <Package className="w-12 h-12 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="card-feira">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total de Cliques</p>
                <p className="text-3xl font-bold text-blue-600">
                  {formatNumber(analytics.general_stats.total_whatsapp_clicks)}
                </p>
              </div>
              <MousePointer className="w-12 h-12 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="card-feira">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Média por Produto</p>
                <p className="text-3xl font-bold text-orange-600">
                  {analytics.general_stats.avg_clicks_per_product.toFixed(1)}
                </p>
              </div>
              <TrendingUp className="w-12 h-12 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs de Analytics */}
      <Tabs defaultValue="products" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="products" className="flex items-center gap-2">
            <Eye className="w-4 h-4" />
            Produtos Mais Vistos
          </TabsTrigger>
          <TabsTrigger value="producers" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Produtores
          </TabsTrigger>
          <TabsTrigger value="categories" className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            Categorias
          </TabsTrigger>
        </TabsList>

        {/* Produtos Mais Vistos */}
        <TabsContent value="products">
          <Card className="card-feira">
            <CardHeader>
              <CardTitle className="text-2xl font-bold text-gray-800">
                Top 10 Produtos Mais Procurados
              </CardTitle>
              <p className="text-gray-600">Produtos com mais cliques no botão "Falar com Produtor"</p>
            </CardHeader>
            <CardContent>
              {analytics.top_products.length === 0 ? (
                <div className="text-center py-8">
                  <Eye className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">Nenhum dado de clique ainda</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {analytics.top_products.map((product, index) => (
                    <div key={product.id} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <Badge className="bg-green-100 text-green-800">
                            #{index + 1}
                          </Badge>
                          <div>
                            <h4 className="font-semibold text-gray-800">{product.name}</h4>
                            <p className="text-sm text-gray-600">
                              por {product.producer_name} • {product.producer_city}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-blue-600">
                            {formatNumber(product.whatsapp_clicks || 0)}
                          </p>
                          <p className="text-sm text-gray-500">cliques</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <span>R$ {product.price.toFixed(2)}</span>
                        <span>{product.category}</span>
                        <span>{product.quantity} disponível</span>
                        {product.average_rating > 0 && (
                          <div className="flex items-center gap-1">
                            <Star className="w-3 h-3 text-yellow-400 fill-current" />
                            <span>{product.average_rating.toFixed(1)}</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="mt-3">
                        <div className="flex justify-between text-sm mb-1">
                          <span>Interesse do consumidor</span>
                          <span>{getClickPercentage(product.whatsapp_clicks || 0, maxProductClicks)}%</span>
                        </div>
                        <Progress 
                          value={getClickPercentage(product.whatsapp_clicks || 0, maxProductClicks)} 
                          className="h-2"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Estatísticas por Produtor */}
        <TabsContent value="producers">
          <Card className="card-feira">
            <CardHeader>
              <CardTitle className="text-2xl font-bold text-gray-800">
                Performance dos Produtores
              </CardTitle>
              <p className="text-gray-600">Estatísticas de interesse por produtor</p>
            </CardHeader>
            <CardContent>
              {analytics.producer_stats.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">Nenhum dado de produtor ainda</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {analytics.producer_stats.map((producer, index) => (
                    <div key={producer._id} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <Badge variant="outline">#{index + 1}</Badge>
                          <div>
                            <h4 className="font-semibold text-gray-800">{producer.producer_name}</h4>
                            <p className="text-sm text-gray-600">{producer.producer_city}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-green-600">
                            {formatNumber(producer.total_clicks || 0)}
                          </p>
                          <p className="text-sm text-gray-500">cliques totais</p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-4 text-center text-sm">
                        <div>
                          <p className="font-medium text-gray-800">{producer.total_products}</p>
                          <p className="text-gray-600">Produtos</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-800">
                            {(producer.avg_clicks_per_product || 0).toFixed(1)}
                          </p>
                          <p className="text-gray-600">Média por produto</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-800">
                            {getClickPercentage(producer.total_clicks || 0, maxProducerClicks)}%
                          </p>
                          <p className="text-gray-600">Do total</p>
                        </div>
                      </div>
                      
                      <div className="mt-3">
                        <Progress 
                          value={getClickPercentage(producer.total_clicks || 0, maxProducerClicks)} 
                          className="h-2"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Estatísticas por Categoria */}
        <TabsContent value="categories">
          <Card className="card-feira">
            <CardHeader>
              <CardTitle className="text-2xl font-bold text-gray-800">
                Performance por Categoria
              </CardTitle>
              <p className="text-gray-600">Quais categorias têm mais interesse dos consumidores</p>
            </CardHeader>
            <CardContent>
              {analytics.category_stats.length === 0 ? (
                <div className="text-center py-8">
                  <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">Nenhum dado de categoria ainda</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {analytics.category_stats.map((category, index) => (
                    <div key={category._id} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <Badge className="bg-orange-100 text-orange-800">#{index + 1}</Badge>
                          <h4 className="font-semibold text-gray-800">{category._id}</h4>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-orange-600">
                            {formatNumber(category.total_clicks || 0)}
                          </p>
                          <p className="text-sm text-gray-500">cliques</p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 text-center text-sm">
                        <div>
                          <p className="font-medium text-gray-800">{category.total_products}</p>
                          <p className="text-gray-600">Produtos na categoria</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-800">
                            {(category.avg_clicks || 0).toFixed(1)}
                          </p>
                          <p className="text-gray-600">Média de cliques</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminAnalytics;